import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Wedding, WeddingCardData } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react'
import { cn } from '@/lib/utils'
import { weddingField } from '@/lib/cms'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ButtonLink } from '@/components/generals/button'
import { Marigold } from '@/components/generals/ornament'
import { SectionHeading } from '@/components/generals/section-heading'
import { WeddingCover } from '@/components/generals/wedding-cover'
import { WeddingCard } from '@/components/generals/wedding-card'
import { ConsultationBand } from '@/components/generals/consultation-band'
import RichText from '@/components/generals/rich-text'
import { getWeddingBySlug, getWeddingCards, getWeddingSlugs } from '@/lib/weddings'

interface WeddingPageProps {
    wedding: Wedding
    more: Array<WeddingCardData>
}

const WeddingPage: FC<WeddingPageProps> = ({ wedding, more }): ReactNode => {
    const { id } = wedding

    return (
        <Layout>
            <Seo
                title={wedding.couple}
                description={wedding.excerpt ?? undefined}
                ogImage={wedding.coverImage ?? undefined}
            />

            <article aria-labelledby='wedding-title'>
                <header className='relative overflow-hidden border-b border-border/60'>
                    <div aria-hidden='true' className='pattern-jaali absolute inset-0 text-gold/[0.06]' />
                    <div aria-hidden='true' className='absolute -right-40 -top-32 size-[36rem] rounded-full bg-marigold/15 blur-3xl' />

                    <div className='relative mx-auto max-w-7xl px-5 pb-20 pt-10 sm:px-8 lg:pb-24 lg:pt-14'>
                        <Link
                            href='/weddings'
                            className='inline-flex items-center gap-2 rounded-full text-sm font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        >
                            <ArrowLeft aria-hidden='true' className='size-4' />
                            All real weddings
                        </Link>

                        <div className='mt-10 grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16'>
                            <div className='relative mx-auto w-full max-w-md lg:max-w-none'>
                                <div aria-hidden='true' className='arch-frame absolute -inset-3 border border-gold/40' />

                                <div className='arch-frame relative aspect-[4/5] overflow-hidden bg-muted shadow-[0_40px_80px_-40px_rgba(74,13,24,0.6)]'>
                                    <WeddingCover
                                        couple={wedding.couple}
                                        image={wedding.coverImage}
                                        imageAlt={wedding.coverImageAlt}
                                        accentColor={wedding.accentColor}
                                        loading='eager'
                                    />
                                </div>
                            </div>

                            <div>
                                {wedding.weddingType && (
                                    <p className='inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-gold'>
                                        <span aria-hidden='true' className='h-px w-8 bg-current' />
                                        <span data-mdlfy-cms={weddingField(id, 'wedding_type')}>{wedding.weddingType}</span>
                                    </p>
                                )}

                                <h1 id='wedding-title' className='mt-5 text-5xl leading-[1.02] text-foreground sm:text-6xl lg:text-7xl'>
                                    <span data-mdlfy-cms={weddingField(id, 'couple')}>{wedding.couple}</span>
                                </h1>

                                <p data-mdlfy-cms={weddingField(id, 'title')} className='mt-5 font-heading text-2xl italic leading-snug text-primary sm:text-3xl'>
                                    {wedding.title}
                                </p>

                                {wedding.excerpt && (
                                    <p data-mdlfy-cms={weddingField(id, 'excerpt')} className='mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground'>
                                        {wedding.excerpt}
                                    </p>
                                )}

                                <dl className='mt-10 grid grid-cols-1 gap-x-8 gap-y-6 border-t border-border pt-8 sm:grid-cols-2'>
                                    {wedding.location && (
                                        <div>
                                            <dt className='text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground'>Location</dt>
                                            <dd className='mt-1.5 font-heading text-2xl text-foreground'>
                                                <span data-mdlfy-cms={weddingField(id, 'location')}>{wedding.location}</span>
                                            </dd>
                                        </div>
                                    )}

                                    {wedding.venue && (
                                        <div>
                                            <dt className='text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground'>Venue</dt>
                                            <dd className='mt-1.5 font-heading text-2xl text-foreground'>
                                                <span data-mdlfy-cms={weddingField(id, 'venue')}>{wedding.venue}</span>
                                            </dd>
                                        </div>
                                    )}

                                    {wedding.weddingMonth && (
                                        <div>
                                            <dt className='text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground'>Celebrated</dt>
                                            <dd className='mt-1.5 font-heading text-2xl text-foreground'>
                                                <span data-mdlfy-cms={weddingField(id, 'wedding_month')}>{wedding.weddingMonth}</span>
                                            </dd>
                                        </div>
                                    )}

                                    {wedding.guestCount && (
                                        <div>
                                            <dt className='text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground'>Guests</dt>
                                            <dd className='mt-1.5 font-heading text-2xl text-foreground'>
                                                <span data-mdlfy-cms={weddingField(id, 'guest_count')}>{wedding.guestCount}</span> guests
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        </div>
                    </div>
                </header>

                <div className='mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-20 lg:py-28'>
                    <div className='min-w-0'>
                        <h2 className='sr-only'>Their story</h2>

                        {wedding.story ? (
                            <RichText html={wedding.story} className='max-w-2xl text-lg leading-relaxed text-foreground/85' />
                        ) : (
                            <p className='text-lg text-muted-foreground'>
                                The full story of this wedding is on its way.
                            </p>
                        )}

                        {wedding.gallery.length > 0 && (
                            <section aria-labelledby='gallery-title' className='mt-16'>
                                <h2 id='gallery-title' className='text-4xl text-foreground'>
                                    From the wedding album
                                </h2>

                                <ul className='mt-8 grid gap-4 sm:grid-cols-2'>
                                    {wedding.gallery.map((src, index) => (
                                        <li
                                            key={`${src}-${index}`}
                                            className={cn('overflow-hidden rounded-2xl bg-muted', index % 3 === 0 ? 'aspect-[16/10] sm:col-span-2' : 'aspect-[4/5]')}
                                        >
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={src}
                                                alt={wedding.galleryAlt[index] || `A moment from the wedding of ${wedding.couple}`}
                                                loading='lazy'
                                                decoding='async'
                                                className='size-full object-cover'
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>

                    <aside aria-label="Wedding details" className='space-y-6 lg:sticky lg:top-28 lg:self-start'>
                        {wedding.events.length > 0 && (
                            <div className='rounded-3xl border border-border bg-card p-7'>
                                <h2 className='text-2xl text-foreground'>
                                    The functions
                                </h2>

                                <ol className='mt-5 space-y-3'>
                                    {wedding.events.map((event, index) => (
                                        <li key={`${event}-${index}`} className='flex items-center gap-3 text-foreground/85'>
                                            <span aria-hidden='true' className='flex size-7 shrink-0 items-center justify-center rounded-full bg-accent font-heading text-base text-primary'>
                                                {index + 1}
                                            </span>
                                            {event}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}

                        {wedding.highlights.length > 0 && (
                            <div className='relative overflow-hidden rounded-3xl bg-maroon p-7 text-ivory'>
                                <div aria-hidden='true' className='pattern-jaali absolute inset-0 text-gold-light/[0.07]' />

                                <h2 className='relative text-2xl text-ivory'>
                                    Signature moments
                                </h2>

                                <ul className='relative mt-5 space-y-4'>
                                    {wedding.highlights.map((highlight, index) => (
                                        <li key={`${highlight}-${index}`} className='flex gap-3 leading-relaxed text-ivory/85'>
                                            <Marigold className='mt-1 size-4 text-marigold' />
                                            {highlight}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className='rounded-3xl border border-gold/30 bg-background p-7'>
                            <p className='font-heading text-2xl leading-snug text-foreground'>
                                Planning something like this?
                            </p>

                            <p className='mt-2 leading-relaxed text-muted-foreground'>
                                Tell us about your families and your dates, and we will share ideas on the first call.
                            </p>

                            <ButtonLink href='/contact' size='sm' className='mt-5'>
                                Start planning
                                <ArrowRight aria-hidden='true' />
                            </ButtonLink>
                        </div>
                    </aside>
                </div>

                {wedding.quote && (
                    <section aria-label="In their words" className='border-y border-border/60 bg-secondary/70 py-20 lg:py-24'>
                        <figure className='mx-auto max-w-4xl px-5 text-center sm:px-8'>
                            <Quote aria-hidden='true' className='mx-auto size-10 text-marigold' />

                            <blockquote className='mt-6'>
                                <p data-mdlfy-cms={weddingField(id, 'quote')} className='font-heading text-3xl leading-snug text-foreground sm:text-4xl'>
                                    {wedding.quote}
                                </p>
                            </blockquote>

                            {wedding.quoteBy && (
                                <figcaption className='mt-6 text-sm font-medium uppercase tracking-[0.22em] text-gold'>
                                    <span data-mdlfy-cms={weddingField(id, 'quote_by')}>{wedding.quoteBy}</span>
                                </figcaption>
                            )}
                        </figure>
                    </section>
                )}
            </article>

            {more.length > 0 && (
                <section id='more-weddings' aria-labelledby='more-weddings-title' className='py-24 lg:py-32'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                            <SectionHeading
                                id='more-weddings-title'
                                eyebrow="Keep exploring"
                                title={<>More <em className='text-primary'>real weddings</em></>}
                            />

                            <ButtonLink href='/weddings' variant='outline' className='self-start lg:self-auto'>
                                View all weddings
                            </ButtonLink>
                        </div>

                        <div className='mt-14 grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3'>
                            {more.map(card => (
                                <WeddingCard key={card.id} wedding={card} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <ConsultationBand />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getWeddingSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Could not list the real weddings:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<WeddingPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true, revalidate: 60 }

    const wedding = await getWeddingBySlug(slug)

    if (!wedding) return { notFound: true, revalidate: 60 }

    let more: Array<WeddingCardData> = []

    try {
        const cards = await getWeddingCards()
        const index = cards.findIndex(card => card.id === wedding.id)
        const ordered = index >= 0 ? [...cards.slice(index + 1), ...cards.slice(0, index)] : cards

        more = ordered.filter(card => card.id !== wedding.id).slice(0, 3)
    }
    catch (error) {
        console.error('Could not load more real weddings:', error instanceof Error ? error.message : error)
    }

    return { props: { wedding, more }, revalidate: 60 }
}

export default WeddingPage
